import { AxiosRequestConfig } from 'axios-cad-nx';
import { v4 as uuidv4 } from 'uuid';
import { m43Headers, type ExtraConfig } from '@m43/shared';
import { getRealUrlPrefix } from '~/session';

type RequestConfig = AxiosRequestConfig & ExtraConfig;

function getCurrentUTCTimestamp(): string {
    return new Date().toISOString();
}

/**
 * Requests declare a target service rather than a base URL, so the client stays
 * agnostic about where each service is mounted.
 */
function baseUrlForService(service: ExtraConfig['service'], prefix: string): string | undefined {
    switch (service) {
        case 'RMS':
            return `${prefix}/rms/api`;
        case 'DEX':
            return `${prefix}/dex/api`;
        case 'USAGE_LOGS':
            return `${prefix}/usage_logs/api`;
        default:
            return undefined;
    }
}

/** Mock client: no auth or correlation headers, and base URLs are origin-relative. */
export function applyMockRequestConfig(config: RequestConfig): RequestConfig {
    const baseURL = baseUrlForService(config.service, '');
    return baseURL ? { ...config, baseURL } : config;
}

export function applyRealRequestConfig(config: RequestConfig): RequestConfig {
    if (config.headers && config.service !== 'RMS') {
        config.headers['x-cad-correlation-id'] = uuidv4();
    }
    if (config.headers) {
        config.headers['x-request-initiated-timestamp'] = getCurrentUTCTimestamp();
    }
    // Declaring `m43` opts the request into operation measurement. The headers let the
    // backend stamp its span, and — on the fetch client — let Datadog's beforeSend read
    // them back off `requestInit` to tag the browser span.
    if (config.m43 && config.headers) {
        Object.assign(config.headers, m43Headers(config.m43));
    }

    const baseURL = baseUrlForService(config.service, getRealUrlPrefix());
    return baseURL ? { ...config, baseURL } : config;
}
